import React, { useState } from "react";
import DataTable from "react-data-table-component";
import { formatDate } from "../../utils/utils";
import { DownloadCloudIcon, SettingsIcon } from "lucide-react";
import Papa from "papaparse";
import CustomLoader from "../CustomLoader";
import NoDataComponent from "../NoDataComponent";
import { useNavigate } from "react-router-dom";

const SubscriptionsTable = ({ data, selected, loading }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();
  const columns = [
    {
      name: "Order Id",
      selector: (row) => row.OrderId,
      sortable: true,
    },
    {
      name: "Plan / Period",
      selector: (row) =>
        `${row.subscription_plan_type}/${row.subscription_plan_period}`,
      sortable: true,
      width: "120px",
    },

    {
      name: "Buyer Details",
      cell: (row) => (
        <div>
          <div>
            <p className="font-semibold mt-3">
              Name : <span className="font-normal">{row?.createdBy?.name}</span>
            </p>
            <p className="font-semibold mt-2">
              Email :{" "}
              <span className="font-normal">{row?.createdBy?.email}</span>
            </p>
            <p className="font-semibold mt-2">
              Mobile :{" "}
              <span className="font-normal">{row?.createdBy?.mobile}</span>
            </p>
            <p className="font-semibold mt-2 mb-3">
              Country :{" "}
              <span className="font-normal">{row?.createdBy?.country}</span>
            </p>
          </div>
        </div>
      ),
      width: "250px",
      sortable: true,
    },
    {
      name: "Expiry Date",
      selector: (row) => formatDate(row?.subscription_end_date),
      sortable: true,
      width: "140px",
    },
    {
      name: "Amount",
      selector: (row) =>
        `${
          row.payment_gateway === "Razorpay"
            ? `₹${row.subscription_amount / 100}`
            : row.payment_gateway === "Stripe"
            ? `$${row.subscription_amount / 100}`
            : `$${row.subscription_amount}`
        }`,
      sortable: true,
    },
    {
      name: "Subscription Period",
      cell: (row) => (
        <div>
          <p className="font-semibold mt-3">
            Start Date :{" "}
            <span className="font-normal">
              {formatDate(row.subscription_start_date)}
            </span>
          </p>
          <p className="font-semibold mt-2 mb-3">
            End Date :{" "}
            <span className="font-normal">
              {formatDate(row.subscription_end_date)}
            </span>
          </p>
        </div>
      ),
      sortable: true,
    },
    {
      name: "Subscription Id",
      selector: (row) => (row.subscription_id ? row.subscription_id : "free"),
      sortable: true,
      width: "180px",
    },
    {
      name: "Gateway",
      selector: (row) => (row.payment_gateway ? row.payment_gateway : "NA"),
      sortable: true,
      width: "100px",
    },
    {
      name: "Status",
      cell: (row) => (
        <div>
          {row.subscription_status === "cancelled" ? (
            <div>
              <p className="font-semibold mt-3">
                Status :{" "}
                <span className="font-normal">{row.subscription_status}</span>
              </p>
              <p className="font-semibold mt-3">
                Cancelled At :{" "}
                <span className="font-normal">
                  {formatDate(row?.subscription_cancelled_at) || ""}
                </span>
              </p>
            </div>
          ) : (
            <p className="font-semibold mt-3">
              Status :{" "}
              <span className="font-normal">{row.subscription_status}</span>
            </p>
          )}
        </div>
      ),
      sortable: true,
    },
    {
      name: "Action",
      cell: (row) => (
        <div>
          <div>
            <div
              onClick={() => navigate(`/subscription/edit/${row._id}`)}
              className="p-2.5 hover:bg-slate-200 hover:text-purple-700 cursor-pointer rounded-full transition-all duration-300"
            >
              <SettingsIcon size={18} className="" />
            </div>
          </div>
        </div>
      ),
      sortable: true,
      width: "80px",
    },
  ];

  const filteredData = data?.filter((sub) => {
    if (
      sub.OrderId.toString().toLowerCase().includes(searchTerm.toLowerCase()) ||
      sub.subscription_plan_type
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      sub.subscription_plan_period
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      (sub.createdBy?.name &&
        sub.createdBy.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (sub.createdBy?.email &&
        sub.createdBy.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (sub.createdBy?.mobile &&
        sub.createdBy.mobile
          .toLowerCase()
          .includes(searchTerm.toLowerCase())) ||
      (sub.createdBy?.country &&
        sub.createdBy.country
          .toLowerCase()
          .includes(searchTerm.toLowerCase())) ||
      formatDate(sub.subscription_end_date)
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      (sub.subscription_id &&
        sub.subscription_id
          .toString()
          .toLowerCase()
          .includes(searchTerm.toLowerCase())) ||
      sub.subscription_status.toLowerCase().includes(searchTerm.toLowerCase())
    ) {
      if (selected === "Active" && sub.subscription_status !== "active") {
        return false;
      } else if (
        selected === "In Active" &&
        sub.subscription_status !== "inactive"
      ) {
        return false;
      } else if (selected === "Free" && sub.subscription_id) {
        return false;
      } else if (
        selected === "Canceled" &&
        sub.subscription_status !== "cancelled"
      ) {
        return false;
      }
      return true;
    }
    return false;
  });

  const customStyles = {
    headCells: {
      style: {
        borderRight: "1px solid #ddd",
        fontWeight: "bold",
        letterSpacing: "0em",
        alignItems: "center",
      },
    },
    cells: {
      style: {
        borderRight: "1px solid #ddd",
        alignItems: "center",
      },
    },
  };

  const handleDownload = () => {
    if (!filteredData || filteredData.length === 0) {
      console.error("No data available for download.");
      return;
    }

    try {
      const csvData = Papa.unparse(
        filteredData.map((sub) => ({
          "Order Id": sub.OrderId,
          "Plan / Period": `${sub.subscription_plan_type}/${sub.subscription_plan_period}`,
          Name: sub?.createdBy?.name,
          Email: sub?.createdBy?.email,
          Mobile: sub?.createdBy?.mobile,
          Country: sub?.createdBy?.country,
          "Expiry Date": formatDate(sub.subscription_end_date),
          Amount: `${sub.subscription_amount / 100}.0`,
          "Start Date": formatDate(sub.subscription_start_date),
          "End Date": formatDate(sub.subscription_end_date),
          "Subscription Id": sub.subscription_id ? sub.subscription_id : "free",
          Gateway: sub.payment_gateway,
          Status: sub.subscription_status,
        }))
      );

      const encodedUri = encodeURI(csvData);
      const link = document.createElement("a");
      link.setAttribute("href", "data:text/csv;charset=utf-8," + encodedUri);
      link.setAttribute("download", `${selected}_Subscriptions_report.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error generating CSV:", error);
    }
  };

  return (
    <div>
      <div className="my-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <input
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="outline-none ring-2 ring-gray-200 2xl:w-[300px] xl:w-[300px] lg:w-[300px] md:w-[300px] w-full h-10 rounded-[8px] indent-3 mt-2 focus:ring-2 focus:ring-indigo-300 transition-all duration-300"
          />
          <div className="flex items-center gap-5">
            {/* <button
              onClick={() => navigate("/subscription/add")}
              className="bg-[#4186F6] text-white h-10 2xl:w-[230px] xl:w-[230px] lg:w-[230px] md:w-[230px] w-full rounded-[8px] flex justify-center items-center gap-2 hover:ring-2  transition-all duration-300"
            >
              <p>Add subscription </p>
            </button> */}
            <button
              onClick={handleDownload}
              className="bg-[#BC3810] text-white h-10 2xl:w-[230px] xl:w-[230px] lg:w-[230px] md:w-[230px] w-full rounded-[8px] flex justify-center items-center gap-2 hover:ring-2 hover:ring-[#f1a791] transition-all duration-300"
            >
              <DownloadCloudIcon />
              <p>Download Report </p>
            </button>
          </div>
        </div>
      </div>

      <div className="ring-2 ring-gray-200 border-gray-200 mt-2">
        <DataTable
          columns={columns}
          data={filteredData}
          pagination
          paginationRowsPerPageOptions={[10, 50, 100, 500]}
          progressPending={loading}
          progressComponent={loading ? <CustomLoader /> : "NoData"}
          customStyles={customStyles}
          noDataComponent={<NoDataComponent />}
          responsive
        />
      </div>
    </div>
  );
};

export default SubscriptionsTable;
